import React, { useEffect, useState } from 'react';
import {
  DatePicker, Form, Input, Modal, Select, message,
} from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import { useLockConfig, checkLocked } from '../hooks/useLockConfig';

export default function ErrorCreateModal({
  open,
  agentUserId,
  agentName,
  campaignNo,
  initialCampaign,
  initialCampaignId,
  initialFormId,
  initialAssignedToId,
  initialExternalRef,
  contactId,
  callDate,
  currentUser,
  navigate,
  onClose,
  onCreated,
}) {
  const [form]         = Form.useForm();
  const lockConfig     = useLockConfig();
  const [saving,            setSaving]            = useState(false);
  const [errorForms,        setErrorForms]        = useState([]);
  const [formsLoading,      setFormsLoading]      = useState(false);
  const [qaUsers,           setQaUsers]           = useState([]);
  const [agentUsers,        setAgentUsers]        = useState([]);
  const [authorizedCampaigns, setAuthorizedCampaigns] = useState([]);
  const [agentNoDisplay,    setAgentNoDisplay]    = useState('');
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const needsAgentSelect = !agentUserId;
  const campaignLocked   = !!campaignNo;

  const loadQaUsers = (campaignId) => {
    const params = { can_edit_errors: 1 };
    if (campaignId) params.campaign_id = campaignId;
    api.get('/api/users', { params }).then((r) => setQaUsers(r.data)).catch(() => {});
  };

  // Load static dropdowns once on mount
  useEffect(() => {
    if (needsAgentSelect && !agentUsers.length)
      api.get('/api/users')
        .then((r) => setAgentUsers(r.data)).catch(() => {});
    if (campaignLocked) {
      api.get('/api/eval-forms/published', {
        params: { form_type: 'error_tracking', campaign_no: campaignNo },
      }).then((r) => setErrorForms(r.data)).catch(() => {});
    } else {
      api.get('/api/agent-stats/options')
        .then((r) => setAuthorizedCampaigns(r.data.campaigns || []))
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  // Pre-fill form on open
  useEffect(() => {
    if (!open) return;
    setAgentNoDisplay('');
    setQaUsers([]);
    setSelectedCampaignId(null);
    setErrorForms([]);
    form.resetFields();
    form.setFieldsValue({
      assigned_to_user_id: initialAssignedToId ?? null,
      form_id:             initialFormId        ?? null,
      campaign_id:         initialCampaignId    ?? null,
      external_ref:        initialExternalRef   ?? null,
      date_of_error:       callDate ? dayjs(callDate) : dayjs(),
    });

    if (!needsAgentSelect) {
      // Agent is known — load their AgentNo for display
      api.get('/api/users', { params: { id: agentUserId } })
        .then((r) => { setAgentNoDisplay(r.data?.[0]?.AgentNos?.[0] || ''); })
        .catch(() => {});
    }

    if (campaignLocked) {
      // Campaign is known upfront — load eligible assignees immediately
      loadQaUsers(initialCampaignId || null);
      if (!initialAssignedToId) form.setFieldValue('assigned_to_user_id', currentUser?.ID ?? null);
      api.get('/api/eval-forms/published', {
        params: { form_type: 'error_tracking', campaign_no: campaignNo },
      }).then((r) => setErrorForms(r.data)).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  const onCampaignChange = (campaignId) => {
    setSelectedCampaignId(campaignId || null);
    form.setFieldValue('form_id', null);
    form.setFieldValue('assigned_to_user_id', null);
    setQaUsers([]);
    setErrorForms([]);
    if (!campaignId) return;
    // Load assignees and forms for the selected campaign
    loadQaUsers(campaignId);
    setFormsLoading(true);
    api.get('/api/eval-forms/published', {
      params: { form_type: 'error_tracking', campaign_id: campaignId },
    })
      .then((r) => setErrorForms(r.data))
      .catch(() => {})
      .finally(() => setFormsLoading(false));
  };

  const handleCreate = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    if (checkLocked(lockConfig, 'error', values.date_of_error?.format('YYYY-MM-DD'))) {
      message.error('This date is before the configured lock date. Error submissions cannot be created for locked dates.');
      return;
    }
    setSaving(true);
    try {
      const campaignIdToSend = values.campaign_id ?? selectedCampaignId;
      const campaignNameToSend = campaignLocked
        ? (initialCampaign || '')
        : (authorizedCampaigns.find((c) => c.ID === campaignIdToSend)?.Name || '');
      const res = await api.post('/api/errors', {
        form_id:             values.form_id,
        employee_user_id:    agentUserId ?? values.agent_user_id,
        campaign_id:         campaignIdToSend,
        campaign:            campaignNameToSend,
        assigned_to_user_id: values.assigned_to_user_id,
        external_ref:        values.external_ref || null,
        source:              values.source || null,
        date_of_error:       values.date_of_error?.format('YYYY-MM-DD') || null,
        contact_id:          contactId || null,
      });
      const newId = res.data.id;
      message.success('Error submission created.');
      onClose();
      if (onCreated) {
        onCreated(newId);
      } else {
        navigate(`/errors/${newId}`);
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to create error submission.');
    } finally {
      setSaving(false);
    }
  };

  const onAgentChange = (val, opt) => {
    setAgentNoDisplay(opt?.agentNos?.[0] || '');
  };

  const agentSelectOptions = (() => {
    const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
    return (vtIds.length > 0 ? agentUsers.filter((u) => vtIds.includes(u.TeamID)) : agentUsers)
      .map((u) => ({
        label:    u.TeamName ? `${u.DisplayName || u.Email} (${u.TeamName})` : (u.DisplayName || u.Email),
        value:    u.ID,
        agentNos: u.AgentNos || [],
      }));
  })();

  const title = agentName ? `Create Error — ${agentName}` : 'Create Error';

  return (
    <Modal maskClosable={false}
      title={title}
      open={open}
      onCancel={onClose}
      onOk={handleCreate}
      confirmLoading={saving}
      okText="Create"
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* Step 1 when standalone: pick the agent first so we know their team */}
        {needsAgentSelect && (
          <Form.Item label="Agent" name="agent_user_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch optionFilterProp="label" allowClear
              placeholder="Select an agent…"
              options={agentSelectOptions}
              onChange={onAgentChange}
              onClear={() => onAgentChange(null, null)}
            />
          </Form.Item>
        )}

        {(needsAgentSelect || agentNoDisplay) && (
          <Form.Item label="Agent No">
            <Input
              value={needsAgentSelect ? agentNoDisplay : agentNoDisplay || '—'}
              readOnly
              bordered={false}
              style={{ paddingLeft: 0, color: '#595959', background: 'transparent' }}
            />
          </Form.Item>
        )}

        {/* Campaign — locked (read-only) when coming from a call log / AI notification */}
        {campaignLocked ? (
          <>
            <Form.Item label="Campaign">
              <Input
                value={initialCampaign || ''}
                readOnly
                bordered={false}
                style={{ paddingLeft: 0, color: '#595959', background: 'transparent' }}
              />
            </Form.Item>
            {/* Hidden field carries the integer ID for the POST */}
            <Form.Item name="campaign_id" hidden><Input /></Form.Item>
          </>
        ) : (
          <Form.Item label="Campaign" name="campaign_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch optionFilterProp="label"
              placeholder="Select a campaign…"
              options={authorizedCampaigns.map((c) => ({ label: c.Name, value: c.ID }))}
              onChange={onCampaignChange}
              allowClear
              onClear={() => onCampaignChange(null)}
            />
          </Form.Item>
        )}

        {/* Assign To: shown once campaign is known — list filtered to users with access to that campaign */}
        {(campaignLocked || selectedCampaignId) && (
          <Form.Item label="Assign To" name="assigned_to_user_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch optionFilterProp="label"
              placeholder="Select a user…"
              options={qaUsers.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))}
            />
          </Form.Item>
        )}

        {/* Error Form — shown once campaign is known */}
        {(campaignLocked || selectedCampaignId) && (
          <Form.Item label="Error Form" name="form_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch optionFilterProp="label"
              loading={formsLoading}
              placeholder="Select an error tracking form…"
              options={errorForms.map((f) => ({ label: `${f.Name} (v${f.Version})`, value: f.ID }))}
              notFoundContent="No published error tracking forms for this campaign."
            />
          </Form.Item>
        )}

        <Form.Item label="Date of Error" name="date_of_error" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
        </Form.Item>

        <Form.Item label="Source" name="source" rules={[{ required: true, message: 'Source is required' }]}>
          <Select placeholder="Select source…" options={[
            { label: 'Ticket Review',  value: 'Ticket Review'  },
            { label: 'Support/Tier 2', value: 'Support/Tier 2' },
            { label: 'Client Email',   value: 'Client Email'   },
            { label: 'Call Review',    value: 'Call Review'    },
            { label: 'Form Response',  value: 'Form Response'  },
          ]} />
        </Form.Item>

        <Form.Item label="External Ref" name="external_ref">
          <Input placeholder="Optional reference number or ID…" allowClear />
        </Form.Item>
      </Form>
    </Modal>
  );
}
